import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Popup,
  Table,
  TableRow,
  TextArea,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AbsenceReason,
  calculateVacationDaysForDateRange,
  ContractInformation,
  DocumentBucket,
  EmploymentHistoryEntry,
  FamilyState,
  generateDropdownOptions,
  PaymentType,
  SchambeckUser,
  Schedule,
  ScheduleType,
  TaxClass,
  useData,
} from 'shared';
import dayjs from 'shared/src/tools/Dayjs';
import { getTranslatedName } from '../../utils/holiday/Holiday.utils';
import { getContractHistoryRows } from '../../utils/user/User.utils';
import { FileOverview } from '../files/FileOverview';

interface ConfidentialInformationProps {
  editUserSchedules: Schedule[];
  allAbsenceReasons: AbsenceReason[];
  editUser: SchambeckUser;
  loggedInUser: SchambeckUser;
  updateContract: (
    key: keyof ContractInformation,
    newValue: Date | string | number | boolean | undefined | EmploymentHistoryEntry[],
  ) => void;
  setEditUser: (value: React.SetStateAction<SchambeckUser>) => void;
}

export const ConfidentailInformation: React.FC<ConfidentialInformationProps> = ({
  editUserSchedules,
  allAbsenceReasons,
  editUser,
  loggedInUser,
  updateContract,
  setEditUser,
}) => {
  const { t } = useTranslation();
  const [rowToEdit, setRowToEdit] = useState<string>();
  const [rowToDelete, setRowToDelete] = useState<string>();
  const { data: allCurrencies } = useData('CURRENCY_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: allOffices } = useData('OFFICES_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allcompanies } = useData('COMPANY_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allUserFunctions } = useData('USER_FUNCTION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOfficialHolidays } = useData('OFFICIAL_HOLIDAYS_ALL', {
    params: { userId: editUser.id },
    config: { fallbackData: [] },
  });

  /**
   * Holds the tableRows for all absences of the editUser
   */
  const absenceRows: TableRow[] = useMemo((): TableRow[] => {
    return editUserSchedules
      .filter(
        (schedule) =>
          schedule.type === ScheduleType.USER_ABSENCE && !!schedule.startDate && !!schedule.endDate,
      )
      .map((schedule) => {
        const reason: AbsenceReason | undefined = allAbsenceReasons.find(
          (reason) => reason.id === schedule.appointmentDetail?.reasonId,
        );
        const reasonName: string = getTranslatedName(reason, loggedInUser.language);
        return {
          id: schedule.id,
          content: [
            reasonName,
            schedule.startDate?.toLocaleDateString('DE-de') || '-',
            schedule.endDate?.toLocaleDateString('DE-de') || '-',
            calculateVacationDaysForDateRange(
              schedule.startDate!,
              schedule.endDate!,
              editUser.contractInformation.workSchedules,
              loadedOfficialHolidays.map((holiday) => dayjs(holiday.date)),
            ) || '-',
            schedule.appointmentDetail?.description || '-',
            schedule.createDate?.toLocaleDateString('DE-de') || '-',
          ],
        };
      });
  }, [
    allAbsenceReasons,
    editUserSchedules,
    editUser.contractInformation,
    loadedOfficialHolidays,
    loggedInUser.language,
  ]);

  /**
   * Holds the rows for all contract Changes
   */
  const contractChangeRows: TableRow[] = useMemo((): TableRow[] => {
    return getContractHistoryRows(
      editUser.contractInformation.contractChanges,
      allUserFunctions,
      allcompanies,
      allOffices,
      rowToEdit,
      setRowToEdit,
      setRowToDelete,
      (updatedEntry: EmploymentHistoryEntry) => {
        setEditUser({
          ...editUser,
          contractInformation: {
            ...editUser.contractInformation,
            contractChanges: editUser.contractInformation.contractChanges.map((entry) =>
              entry.id === rowToEdit ? updatedEntry : entry,
            ),
          },
        });
      },
    );
  }, [allOffices, allUserFunctions, allcompanies, editUser, rowToEdit, setEditUser]);
  return (
    <Box title={t('pages.user.create.secretInformation')} id="secret-information">
      {rowToDelete && (
        <Popup
          title={t('pages.user.create.deleteHistoryPopup.title')}
          isOpen={!!rowToDelete}
          onClose={() => setRowToDelete(undefined)}
          buttons={[
            <Button
              onClick={() => setRowToDelete(undefined)}
              value={t('general.buttons.cancel')}
            />,
            <Button
              onClick={() => {
                updateContract(
                  'contractChanges',
                  editUser.contractInformation.contractChanges.filter(
                    (change) => change.id !== rowToDelete,
                  ),
                );
                setRowToDelete(undefined);
              }}
              value={t('general.buttons.confirm')}
            />,
          ]}
        >
          {t('pages.user.create.deleteHistoryPopup.content')}
        </Popup>
      )}
      <div className="three-columns">
        <Dropdown
          options={Object.values(PaymentType).map((type) => ({
            label: t(`general.paymentType.${type}`),
            value: type,
          }))}
          onChange={(newValue) => updateContract('paymentType', newValue)}
          label={t(`pages.user.create.contract.paymentType`)}
          selectedOption={editUser.contractInformation.paymentType}
        />
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Input
            label={t(
              `pages.user.create.contract.${
                editUser.contractInformation.paymentType === PaymentType.SALARY ? 'salary' : 'wage'
              }`,
            )}
            value={editUser.contractInformation.salary}
            onChangeNumber={(newValue) => updateContract('salary', newValue)}
            type="number"
          />
          <Dropdown
            options={generateDropdownOptions(allCurrencies, 'symbol', 'id')}
            onChange={(newValue) => updateContract('salaryCurrency', newValue)}
            label={t(`pages.user.create.contract.currency`)}
            selectedOption={editUser.contractInformation.salaryCurrency}
          />
        </div>
        <div>
          <Checkbox
            isChecked={editUser.contractInformation.bonusPayments}
            onCheck={(checked) => updateContract('bonusPayments', checked)}
            label={t(`pages.user.create.contract.bonusPayments`)}
          />
          <Checkbox
            isChecked={editUser.contractInformation.executiveFunction}
            onCheck={(checked) => updateContract('executiveFunction', checked)}
            label={t(`pages.user.create.contract.executiveFunction`)}
          />
        </div>
      </div>
      <TextArea
        noNormalize={true}
        onChange={(value) => updateContract('additionalBenefits', value)}
        label={t(`pages.user.create.contract.additionalBenefits`)}
        value={editUser.contractInformation.additionalBenefits}
        resizable
      />
      <div className="three-columns">
        <Dropdown
          clearable
          label={t('pages.user.create.deputy')}
          selectedOption={editUser.deputyId}
          options={generateDropdownOptions(allUserFunctions, 'title', 'id')}
          onChange={(deputyId) => setEditUser({ ...editUser, deputyId })}
        />
        <Dropdown
          clearable
          label={t('pages.user.create.supervisor')}
          selectedOption={editUser.supervisorId}
          options={generateDropdownOptions(allUserFunctions, 'title', 'id')}
          onChange={(supervisorId) => setEditUser({ ...editUser, supervisorId: supervisorId })}
        />
        <Input
          type="text"
          value={editUser.privateInformation.taxId}
          onChange={(taxId) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                taxId,
              },
            })
          }
          label={t('pages.user.create.taxId')}
        />
        <Input
          type="text"
          value={editUser.privateInformation.healthInsurance}
          onChange={(healthInsurance) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                healthInsurance,
              },
            })
          }
          label={t('pages.user.create.healthInsurance')}
        />
        <Input
          type="text"
          value={editUser.privateInformation.religion}
          onChange={(religion) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                religion,
              },
            })
          }
          label={t('pages.user.create.religion')}
        />
        <Checkbox
          isChecked={editUser.privateInformation.children}
          onCheck={(children) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                children,
              },
            })
          }
          label={t('pages.user.create.children')}
        />
        <Dropdown
          options={Object.values(TaxClass).map((tax) => ({
            label: t(`pages.user.create.taxClasses.${tax}`),
            value: tax,
          }))}
          selectedOption={editUser.privateInformation.taxClass}
          label={t('pages.user.create.taxClass')}
          onChange={(tax) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                taxClass: tax as TaxClass,
              },
            })
          }
        />
        <Dropdown
          options={Object.values(FamilyState).map((fam) => ({
            label: t(`general.familyStates.${fam}`),
            value: fam,
          }))}
          selectedOption={editUser.privateInformation.familyState}
          label={t('pages.user.create.familyState')}
          onChange={(state) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                familyState: state as FamilyState,
              },
            })
          }
        />
        <Input
          type="text"
          value={editUser.privateInformation.bankAccount.owner}
          onChange={(owner) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                bankAccount: {
                  ...editUser.privateInformation.bankAccount,
                  owner,
                },
              },
            })
          }
          label={t('pages.user.create.bankAccount.owner')}
        />
        <Input
          type="text"
          value={editUser.privateInformation.bankAccount.iban}
          onChange={(iban) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                bankAccount: {
                  ...editUser.privateInformation.bankAccount,
                  iban,
                },
              },
            })
          }
          label={t('pages.user.create.bankAccount.iban')}
        />
        <Input
          type="text"
          value={editUser.privateInformation.bankAccount.bic}
          onChange={(bic) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                bankAccount: {
                  ...editUser.privateInformation.bankAccount,
                  bic,
                },
              },
            })
          }
          label={t('pages.user.create.bankAccount.bic')}
        />
        <Input
          type="text"
          value={editUser.privateInformation.disabledLevel}
          onChange={(disabledLevel) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                disabledLevel,
              },
            })
          }
          label={t('pages.user.create.disabledLevel')}
        />
        <Checkbox
          isChecked={editUser.privateInformation.severlyDisabled}
          onCheck={(severlyDisabled) =>
            setEditUser({
              ...editUser,
              privateInformation: {
                ...editUser.privateInformation,
                severlyDisabled,
              },
            })
          }
          label={t('pages.user.create.severlyDisabled')}
        />
      </div>
      <div className="user-create__absences">
        <p className="user-create__absences__header">{t('pages.user.create.absences')}</p>
        <Table
          paginationConfig={{ pageSize: 5 }}
          hideTopFilter
          rows={absenceRows}
          header={
            t('pages.user.create.absenceHeaders', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </div>
      <div className="user-create__confidential-files">
        <p className="user-create__confidential-files__header">
          {t('pages.user.create.confidentialDocuments')}
        </p>
        <FileOverview
          bucket={DocumentBucket.USER}
          path={`${editUser.id}/confidential/`}
          userId={loggedInUser.id}
          isConfidential
        />
      </div>
      <Table
        header={
          t('pages.user.create.historyHeader', {
            returnObjects: true,
          }) as TableHeader[]
        }
        rows={contractChangeRows}
      />
    </Box>
  );
};
